import { IncotermGoupEnum } from '@types'
import { ImplementationTimeUtils } from '@utils'

import { api } from './axios'

const { companyStartOfDay, companyStartOfMonth } = ImplementationTimeUtils

// Weekly metrics types
export interface WeeklyMetricDay {
  date: string | Date
  orders: number
  breaches: number
}

export interface WeeklyMetricSupplier {
  supplierId: number
  tradeName: string
  days: WeeklyMetricDay[]
}

export interface WeeklyMetricsResponse {
  suppliers: WeeklyMetricSupplier[]
  /** Per-supplier TRANSFERENCE breakdown when trip-type filter includes FOB; omitted for CFR-only. */
  transfers?: WeeklyMetricSupplier[]
}

// Daily metrics types
export interface DailyCarrierMetric {
  carrierId: number
  tradeName: string
  confirmedOrders: number
}

export interface DailySupplierMetric {
  supplierId: number
  tradeName: string
  orders: number
  breaches: number
  carriers: DailyCarrierMetric[]
}

export interface DailyMetricsResponse {
  suppliers: DailySupplierMetric[]
}

export type MonthlyComplianceStatusParam = 'CONFIRMED' | 'TRANSIT' | 'DELIVERED'

export interface MonthlyComplianceDayResponse {
  dayDate: string
  real: number
  plan: number
  compliance: number
}

export interface MonthlyComplianceResponse {
  days: MonthlyComplianceDayResponse[]
}

export interface MonthlyComplianceByZoneItemResponse {
  zone: string
  plan: number
  real: number
  compliance: number
}

export interface MonthlyComplianceByZoneResponse {
  zones: MonthlyComplianceByZoneItemResponse[]
}

const getWeekly = async ({
  weekStart,
  tripType,
  gmtZone = 0,
  supplierId,
  carrierId,
}: {
  weekStart: Date
  tripType: IncotermGoupEnum | IncotermGoupEnum[]
  gmtZone?: number
  supplierId?: number
  carrierId?: number
}): Promise<WeeklyMetricsResponse> => {
  const response = await api.get<WeeklyMetricsResponse>('/metrics/suppliers/weekly', {
    params: {
      weekStart: companyStartOfDay(weekStart, gmtZone).toISOString(),
      tripType,
      ...(supplierId !== undefined ? { supplierId } : {}),
      ...(carrierId !== undefined ? { carrierId } : {}),
    },
  })
  return response.data
}

const getDaily = async ({
  date,
  tripType,
  gmtZone = 0,
  supplierId,
  carrierId,
}: {
  date: Date
  tripType: IncotermGoupEnum[]
  gmtZone?: number
  supplierId?: number
  carrierId?: number
}): Promise<DailyMetricsResponse> => {
  const response = await api.get<DailyMetricsResponse>('/metrics/suppliers/daily', {
    params: {
      date: companyStartOfDay(date, gmtZone).toISOString(),
      tripType,
      ...(supplierId !== undefined ? { supplierId } : {}),
      ...(carrierId !== undefined ? { carrierId } : {}),
    },
  })
  return response.data
}

const getMonthlyCompliance = async ({
  monthStart,
  tripType,
  complianceStatuses,
  gmtZone = 0,
  supplierId,
  carrierId,
}: {
  monthStart: Date
  tripType: IncotermGoupEnum[]
  complianceStatuses: MonthlyComplianceStatusParam[]
  gmtZone?: number
  supplierId?: number
  carrierId?: number
}): Promise<MonthlyComplianceResponse> => {
  const response = await api.get<MonthlyComplianceResponse>('/metrics/monthly-compliance', {
    params: {
      monthStart: companyStartOfMonth(monthStart, gmtZone).toISOString(),
      tripType,
      complianceStatuses,
      ...(supplierId !== undefined ? { supplierId } : {}),
      ...(carrierId !== undefined ? { carrierId } : {}),
    },
  })
  return response.data
}

const getMonthlyComplianceByZone = async ({
  monthStart,
  tripType,
  complianceStatuses,
  gmtZone = 0,
  supplierId,
  carrierId,
}: {
  monthStart: Date
  tripType: IncotermGoupEnum[]
  complianceStatuses: MonthlyComplianceStatusParam[]
  gmtZone?: number
  supplierId?: number
  carrierId?: number
}): Promise<MonthlyComplianceByZoneResponse> => {
  const response = await api.get<MonthlyComplianceByZoneResponse>(
    '/metrics/monthly-compliance-by-zone',
    {
      params: {
        monthStart: companyStartOfMonth(monthStart, gmtZone).toISOString(),
        tripType,
        complianceStatuses,
        ...(supplierId !== undefined ? { supplierId } : {}),
        ...(carrierId !== undefined ? { carrierId } : {}),
      },
    },
  )
  return response.data
}

export interface MonthlySupplierMetricsResponse {
  suppliers: DailySupplierMetric[]
}

const getMonthlySuppliers = async ({
  monthStart,
  tripType,
  complianceStatuses,
  gmtZone = 0,
  supplierId,
  carrierId,
}: {
  monthStart: Date
  tripType: IncotermGoupEnum[]
  complianceStatuses: MonthlyComplianceStatusParam[]
  gmtZone?: number
  supplierId?: number
  carrierId?: number
}): Promise<MonthlySupplierMetricsResponse> => {
  const response = await api.get<MonthlySupplierMetricsResponse>('/metrics/suppliers/monthly', {
    params: {
      monthStart: companyStartOfMonth(monthStart, gmtZone).toISOString(),
      tripType,
      complianceStatuses,
      ...(supplierId !== undefined ? { supplierId } : {}),
      ...(carrierId !== undefined ? { carrierId } : {}),
    },
  })
  return response.data
}

export const MetricsApi = {
  getWeekly,
  getDaily,
  getMonthlyCompliance,
  getMonthlyComplianceByZone,
  getMonthlySuppliers,
}
